import {
  siElement,
  siJitsi,
  siMatrix,
  siMattermost,
  siMoodle,
  siN8n,
  siNextcloud,
  siOdoo,
  siVaultwarden,
  siWagtail,
  siWordpress,
} from 'simple-icons';
import type { CSSProperties } from 'react';
import { darkTokens, lightTokens } from '../tokens';

/**
 * The logos of the applications the platform deploys, as the template tiles
 * of « Créer » show them (createPage/TemplateLogo.tsx, mockup « D1 »,
 * 2026-10-19), keyed by a slug a template may also name in its
 * `waadoo.ovh/logo` annotation (createPage/cardPresentation.ts), as an
 * `AppType` may (shared/appIcons.ts). Each slug is also the icons registry's
 * key of the logo, and `<slug>-instance` of the glyph of an instance's address
 * (iconBundle.tsx, `appLogoIcons.tsx`).
 *
 * The paths are Simple Icons' (https://simpleicons.org, the `simple-icons`
 * package), released under CC0 1.0, on a 24x24 viewBox, single-colour, with
 * each brand's own colour. Named imports only: the package is ESM with
 * `"sideEffects": false`, so the bundler keeps these few icons and drops the
 * other three thousand (measured in CUSTOMIZATIONS.md, 2026-10-19). The
 * logos are trademarks of their owners, shown here, in this internal tool,
 * to name the application a template deploys.
 *
 * OpenCloud and Tuleap are not in Simple Icons: a lettered tile in a fixed
 * colour stands in for them until their official logos are provided.
 */
export type AppLogo =
  | { kind: 'path'; title: string; hex: string; path: string }
  | { kind: 'letters'; title: string; hex: string; letters: string };

function simpleIcon(icon: { title: string; hex: string; path: string }) {
  return {
    kind: 'path',
    title: icon.title,
    hex: `#${icon.hex}`,
    path: icon.path,
  } as const;
}

export const APP_LOGOS = {
  odoo: simpleIcon(siOdoo),
  nextcloud: simpleIcon(siNextcloud),
  moodle: simpleIcon(siMoodle),
  n8n: simpleIcon(siN8n),
  element: simpleIcon(siElement),
  jitsi: simpleIcon(siJitsi),
  vaultwarden: simpleIcon(siVaultwarden),
  wordpress: simpleIcon(siWordpress),
  matrix: simpleIcon(siMatrix),
  wagtail: simpleIcon(siWagtail),
  mattermost: simpleIcon(siMattermost),
  opencloud: {
    kind: 'letters',
    title: 'OpenCloud',
    hex: '#1f6feb',
    letters: 'OC',
  },
  tuleap: { kind: 'letters', title: 'Tuleap', hex: '#e5534b', letters: 'T' },
} satisfies Record<string, AppLogo>;

export type AppLogoSlug = keyof typeof APP_LOGOS;

export function isAppLogoSlug(value: string): value is AppLogoSlug {
  return Object.prototype.hasOwnProperty.call(APP_LOGOS, value);
}

/** The share of the logo's colour in its tile, over the card. */
export const LOGO_TINT = 0.12;

/** The contrast a logo keeps on its tile: a graphic, WCAG 1.4.11. */
export const LOGO_MIN_CONTRAST = 3;

/**
 * Under this lightness (the plain weighted sum of the sRGB channels, the
 * mockup's measure), a brand colour disappears on the dark card — the black
 * logos (Vaultwarden, Matrix) — and takes the primary ink there instead.
 */
const DARK_MODE_MIN_LIGHTNESS = 0.25;

function channels(hex: string): [number, number, number] {
  return [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

function toHex(rgb: number[]): string {
  return `#${rgb
    .map(channel => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** `color-mix(in srgb, a <weight>, b)`: per sRGB channel, as CSS mixes. */
export function mixColours(a: string, b: string, weight: number): string {
  const [ca, cb] = [channels(a), channels(b)];
  return toHex(ca.map((channel, i) => channel * weight + cb[i] * (1 - weight)));
}

/** WCAG 2.x relative luminance. */
function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map(value => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

function lightness(hex: string): number {
  const [r, g, b] = channels(hex).map(value => value / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The tile behind a logo of colour `ink`, on `card` (`.waadoo-create-logo`). */
export function logoTile(ink: string, card: string): string {
  return mixColours(ink, card, LOGO_TINT);
}

/**
 * `hex`, moved towards `target` (black on the light card, white on the dark
 * one) by steps of 5 % until it keeps `LOGO_MIN_CONTRAST` on its own tile.
 * Fully moved, it is black or white on a 12 % tint of itself over the card:
 * far above 3:1 on both cards.
 */
function readableOn(hex: string, card: string, target: string): string {
  let ink = hex;
  for (let step = 1; step <= 20; step++) {
    if (contrastRatio(ink, logoTile(ink, card)) >= LOGO_MIN_CONTRAST) break;
    ink = mixColours(target, hex, step / 20);
  }
  return ink;
}

/**
 * The colour of a logo in each mode, `#rrggbb`: its brand colour, darkened
 * on the light card or lightened on the dark one only as far as 3:1 on its
 * tile needs; on the dark card, a near-black brand colour gives way to the
 * primary ink. The cards' and the ink's values are tokens.ts's, which
 * components/ui/tokens.test.ts keeps equal to styles.css's.
 */
export function logoInks(hex: string): { light: string; dark: string } {
  const darkCard = darkTokens['--bui-bg-neutral-1'];
  return {
    light: readableOn(hex, lightTokens['--bui-bg-neutral-1'], '#000000'),
    dark:
      lightness(hex) < DARK_MODE_MIN_LIGHTNESS
        ? darkTokens['--bui-fg-primary']
        : readableOn(hex, darkCard, '#ffffff'),
  };
}

/** The colour of a logo in each mode, read by styles.css. */
export type LogoStyle = CSSProperties &
  Record<'--waadoo-app-colour' | '--waadoo-app-colour-dark', string>;

/**
 * `logoInks` of `slug` as the two custom properties styles.css reads — on a
 * template's tile (`.waadoo-create-logo`), a project's tile
 * (`.waadoo-home-project-tile[data-app-logo]`) and the logo itself
 * (`.waadoo-app-logo`), each picking the one of the page's mode.
 */
export function logoStyle(slug: AppLogoSlug): LogoStyle {
  const inks = logoInks(APP_LOGOS[slug].hex);
  return {
    '--waadoo-app-colour': inks.light,
    '--waadoo-app-colour-dark': inks.dark,
  };
}
